import type { ConnectorActionChange, ConnectorActionRow, ConnectorActionState } from "../../shared/types";

/** Setting more than this many at once asks first. */
export const ASK_OVER = 50;
/** A long area draws this many, and this many more on scroll. */
export const AREA_PAGE = 50;
/** A list this long opens with its areas folded. */
export const OPEN_AREAS_UP_TO = 100;

export type ActionArea = { area: string; rows: ConnectorActionRow[] };
export type ActionGroup = { key: "write" | "read"; title: string; rows: ConnectorActionRow[]; areas: ActionArea[] };

export const rowKey = (a: Pick<ConnectorActionRow, "method" | "paths">) => `${a.method} ${a.paths[0]}`;

/** The rows a search and Changed only leave: a name or any path holding the words, in any case. */
export function filterActions(actions: ConnectorActionRow[], query: string, changedOnly: boolean): ConnectorActionRow[] {
  const q = query.trim().toLowerCase();
  // A person's brake reads Always allow and is locked, but it is a rule the connector holds: Changed only counts it.
  return actions.filter((a) => (!changedOnly || a.state !== "allow" || a.lock?.kind === "person") && (!q || a.name.toLowerCase().includes(q) || a.paths.some((p) => p.toLowerCase().includes(q)) || `${a.method} ${a.paths[0]}`.toLowerCase().includes(q)));
}

const inAreas = (rows: ConnectorActionRow[]): ActionArea[] => {
  const areas = new Map<string, ConnectorActionRow[]>();
  for (const a of rows) areas.set(a.area, [...(areas.get(a.area) ?? []), a]);
  return [...areas].map(([area, rows]) => ({ area, rows }));
};

/** Write/delete first, then Read-only, each by area in the list's own order. A group with nothing in it is left out. */
export function groupActions(actions: ConnectorActionRow[]): ActionGroup[] {
  const groups: ActionGroup[] = [
    { key: "write", title: "Write/delete", rows: actions.filter((a) => !a.readOnly), areas: [] },
    { key: "read", title: "Read-only", rows: actions.filter((a) => a.readOnly), areas: [] },
  ];
  return groups.filter((g) => g.rows.length).map((g) => ({ ...g, areas: inAreas(g.rows) }));
}

/** What a group's header says of its rows: the one state they share, Mixed, or null with none it could set. */
export function sharedState(rows: ConnectorActionRow[]): ConnectorActionState | "mixed" | null {
  const open = rows.filter((a) => !a.lock);
  if (!open.length) return null;
  return open.every((a) => a.state === open[0]!.state) ? open[0]!.state : "mixed";
}

/**
 * What setting a whole group to `state` changes: the rows it can, that aren't on it already. An action added by hand stays
 * out of Always allow, which would take it away: it's removed on its own row.
 */
export function changesFor(rows: ConnectorActionRow[], state: ConnectorActionState): ConnectorActionChange[] {
  return rows
    .filter((a) => !a.lock && a.state !== state && !(a.added && state === "allow"))
    .map((a) => ({ method: a.method, paths: a.paths, state, ...(state === "approval" && a.askOnSchedules ? { askOnSchedules: true } : {}) }));
}

/** The rows as they read once `changes` are saved, so a click shows at once. An added row set to Always allow is gone. */
export function withChanges(actions: ConnectorActionRow[], changes: ConnectorActionChange[]): ConnectorActionRow[] {
  const byRow = new Map(changes.map((c) => [rowKey(c), c]));
  return actions.flatMap((a) => {
    const c = byRow.get(rowKey(a));
    if (!c) return [a];
    return a.added && c.state === "allow" ? [] : [{ ...a, state: c.state, askOnSchedules: Boolean(c.askOnSchedules) }];
  });
}
